import React from 'react';

const AboutMinimal = () => {
  const stats = [
    { value: '50K+', label: 'Followers grown (Berlin Fashion Week)' },
    { value: '€200K+', label: 'Paid social budget managed' },
    { value: '300%', label: 'Average client ROI' },
    { value: '150+', label: 'Campaigns delivered' },
  ];

  const expertise = [
    { area: 'Paid Social', detail: 'Meta & TikTok ads — creative testing, Advantage+, retargeting, and profitable scaling.' },
    { area: 'Organic Growth', detail: 'Instagram, TikTok & Reels strategies that turn strangers into engaged communities.' },
    { area: 'SEO & Content', detail: 'AI-assisted keyword research, topic clusters, and content built to rank and convert.' },
    { area: 'Email & Automation', detail: 'Behavioural flows — welcome, abandoned cart, post-purchase — that run on autopilot.' },
    { area: 'Performance Marketing', detail: 'Full-funnel campaigns measured on CAC, LTV, and ROAS — tied to real revenue.' },
    { area: 'AI-Driven Strategy', detail: 'Integrating modern AI tools to move faster than traditional agencies without losing the human voice.' },
  ];

  return (
    <section id="about" className="bg-black border-y border-tertiary" data-testid="about-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        {/* Section Label */}
        <div className="mb-16">
          <span className="text-sm text-tertiary font-sans uppercase tracking-wider">About</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Portrait */}
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-lg border border-tertiary">
              <img
                src="/images/portfolio/aryan-portrait.jpg"
                alt="Aryan Shivhare, Berlin-based digital marketer"
                className="w-full h-auto object-cover hover:scale-[1.02] transition-transform duration-700"
                data-testid="about-portrait"
              />
            </div>
          </div>

          {/* Bio */}
          <div className="lg:col-span-7 space-y-6">
            <h2 className="font-serif text-3xl lg:text-4xl text-gold leading-tight" data-testid="about-heading">
              Berlin-based, AI-driven digital marketer
            </h2>

            <div className="space-y-5 text-secondary font-sans text-base leading-relaxed">
              <p>
                I’m Aryan Shivhare — a performance marketer and content creator based in Berlin. For
                the past several years I’ve helped fashion labels, e-commerce brands, local
                businesses, and founders grow with full-funnel strategy that blends paid social,
                organic growth, SEO, email, and AI-powered experimentation. My work sits at the
                intersection of creativity and data: bold creative that stops the scroll, backed by
                rigorous testing that scales what actually works.
              </p>
              <p>
                The numbers tell part of the story. I’ve managed over €200K in paid social budgets
                across Meta and TikTok, grew Berlin Fashion Week’s Instagram from 5,000 to 50,000+
                engaged followers in eight months, and delivered 150+ campaigns for European brands —
                averaging a 300% return for clients. But what I’m most proud of isn’t any single
                metric; it’s the repeatable systems I build that keep growing brands long after a
                campaign ends.
              </p>
              <p>
                I also work behind the lens as a photographer and content creator, which means I
                understand both the strategy <em>and</em> the craft — from the data spreadsheet to
                the shot list. That dual perspective lets me brief, produce, and scale content most
                marketers have to outsource. Today I help ambitious brands build growth engines that
                compound, combining performance marketing fundamentals with the latest AI tools to
                move faster, leaner, and smarter than traditional agencies.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-8 pt-8 border-t border-tertiary mt-8" data-testid="about-stats">
              {stats.map((stat) => (
                <div key={stat.label} className="space-y-1">
                  <div className="font-serif text-3xl text-gold">{stat.value}</div>
                  <div className="text-xs text-tertiary font-sans uppercase tracking-wide leading-relaxed">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Areas of Expertise */}
        <div className="mt-20 lg:mt-28" data-testid="about-expertise">
          <h3 className="font-serif text-2xl lg:text-3xl text-gold mb-10">Areas of expertise</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-tertiary border border-tertiary rounded-lg overflow-hidden">
            {expertise.map((item) => (
              <div key={item.area} className="bg-black p-8 hover:bg-zinc-950 transition-colors">
                <div className="font-serif text-lg text-white mb-2">{item.area}</div>
                <p className="text-sm text-tertiary font-sans leading-relaxed">{item.detail}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How I Work */}
        <div className="mt-20 lg:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="about-approach">
          <div className="lg:col-span-4">
            <h3 className="font-serif text-2xl lg:text-3xl text-gold">How I work</h3>
          </div>
          <div className="lg:col-span-8 space-y-6 text-secondary font-sans text-base leading-relaxed">
            <p>
              I treat every campaign as an experiment, not a guess. That means starting with clear
              goals, testing relentlessly, reading the data honestly, and doubling down on what
              works — usually within days, not months. It’s the opposite of the slow,
              retainer-driven agency model.
            </p>
            <p>
              I’m based in Berlin and understand this market intimately — its fashion and creative
              scene, its multilingual, discerning audience, and the local moments that move people.
              Whether you’re a startup with a tight budget or an established brand ready to scale, I
              build a plan around what you can actually spend and the outcomes you actually need.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutMinimal;
